import * as React from 'react';
import { useTheme } from '@leagueplatform/genesis-core';
import { Circle, Polygon } from '@visx/shape';

export const shapes = ['circle', 'square'] as const;
export type ShapeType = (typeof shapes)[number];

type ShapeProps = {
  x: number;
  y: number;
  radius: number;
  shape: ShapeType;
  fill: string;
  ariaLabel?: string;
  strokeWidth?: number;
  strokeColor?: string;
};

function calculateAdjustedCircleRadius(radius: number, strokeWidth: number) {
  const adjustedRadius = radius + strokeWidth / 2;
  return adjustedRadius;
}

function calculateAdjustedRectSize(size: number, strokeWidth: number) {
  const adjustedSize = size + strokeWidth;
  return adjustedSize;
}

const shapeMap: Record<
  ShapeType,
  (params: Omit<ShapeProps, 'shape'>) => JSX.Element
> = {
  circle: ({ x, y, radius, fill, ariaLabel, strokeWidth = 0, strokeColor }) => (
    <Circle
      cx={x}
      cy={y}
      r={calculateAdjustedCircleRadius(radius, strokeWidth)}
      strokeWidth={strokeWidth}
      stroke={strokeColor}
      vectorEffect="non-scaling-stroke"
      fill={fill}
      aria-label={ariaLabel}
      style={{ pointerEvents: 'none' }}
    />
  ),
  square: ({ x, y, radius, fill, ariaLabel, strokeWidth = 0, strokeColor }) => (
    <Polygon
      sides={4}
      rotate={90}
      center={{ x, y }}
      size={calculateAdjustedRectSize(radius, strokeWidth)}
      strokeWidth={strokeWidth}
      vectorEffect="non-scaling-stroke"
      stroke={strokeColor}
      fill={fill}
      aria-label={ariaLabel}
      style={{ pointerEvents: 'none' }}
    />
  ),
};

export function Shape({ shape, ...props }: ShapeProps) {
  return shapeMap[shape](props);
}

type UseColorMapParams = {
  shape: ShapeType;
};

export function useShapeTheme({ shape }: UseColorMapParams) {
  const theme = useTheme();

  const colorMap: Record<ShapeType, { light: string; dark: string }> = {
    circle: {
      light:
        theme.colors.dataVizCategoricalOne ??
        theme.colors.decorativeBrandPrimaryBrightest,
      dark:
        theme.colors.dataVizCategoricalTwo ??
        theme.colors.decorativeBrandPrimaryDark,
    },
    square: {
      light:
        theme.colors.dataVizCategoricalFour ??
        theme.colors.decorativeBrandSecondaryBrightest,
      dark:
        theme.colors.dataVizCategoricalFive ??
        theme.colors.decorativeBrandSecondaryDark,
    },
  };

  return colorMap[shape];
}

type ShapeIconProps = {
  shape: ShapeType;
};

export function ShapeIcon({ shape }: ShapeIconProps) {
  const shapeTheme = useShapeTheme({ shape });
  const theme = useTheme();

  return (
    <svg
      height={theme.sizes.threeQuarters.value}
      width={theme.sizes.threeQuarters.value}
      viewBox="0 0 16 16"
    >
      <Shape shape={shape} x={8} y={8} radius={8} fill={shapeTheme.dark} />;
    </svg>
  );
}
